import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle, FlexContainer } from '../../styles/Layout';
import { FaChartLine, FaBuilding, FaUsers, FaCubes, FaLaptopCode, FaRocket } from 'react-icons/fa';
import developmentAgencyImage from '../../assets/images/erp/image.png.webp';

const AboutSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 20%, rgba(0, 212, 255, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(255, 0, 128, 0.05) 0%, transparent 50%);
    z-index: 1;
  }
`;

const AboutContent = styled(FlexContainer)`
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
`;

const AboutImage = styled(motion.div)`
  flex: 1;
  position: relative;
  min-height: 500px;
  border-radius: 20px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 20px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(255, 0, 128, 0.1) 100%);
    border-radius: 20px;
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
    backdrop-filter: blur(2px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    margin-bottom: var(--spacing-xl);
    min-height: 300px;
  }
`;

const AboutInfo = styled(motion.div)`
  max-width: 1000px;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const AboutTitle = styled.h3`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: var(--spacing-lg);
  position: relative;
  background: linear-gradient(135deg, var(--text-primary) 0%, var(--neon-blue) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    border-radius: 2px;
  }
`;

const AboutDescription = styled.p`
  margin-bottom: var(--spacing-lg);
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-secondary);
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-lg);
  margin-top: var(--spacing-xl);
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertiseItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, transparent 100%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%);
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.color || 'var(--neon-blue)'};
  }
`;

const ExpertiseIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.background || 'var(--neon-blue)'} 0%, ${props => props.background || 'var(--neon-blue)'}80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-lg);
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 8px 20px ${props => props.background || 'var(--neon-blue)'}40;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    border-radius: 16px;
  }
`;

const ExpertiseContent = styled.div`
  h4 {
    margin-bottom: var(--spacing-sm);
    font-size: 1.2rem;
    color: var(--text-primary);
    font-weight: 600;
  }
  
  p {
    margin-bottom: 0;
    font-size: 0.95rem;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const StatsContainer = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-xl);
  gap: var(--spacing-lg);
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1;
  min-width: 120px;
  text-align: center;
  padding: var(--spacing-lg);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  }
  
  .number {
    font-size: 2.5rem;
    font-weight: 900;
    background: linear-gradient(135deg, var(--neon-green) 0%, var(--neon-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
    line-height: 1;
  }
  
  .label {
    font-size: 0.9rem;
    color: var(--text-muted);
    margin-top: var(--spacing-sm);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const About = () => {
  const expertiseItems = [
    {
      icon: <FaChartLine />,
      title: "Algorithmic Trading Platforms",
      description: "Real-time data integration with advanced analytics and AI-powered trading algorithms",
      background: "var(--neon-green)",
      color: "rgba(0, 255, 136, 0.3)",
    },
    {
      icon: <FaBuilding />,
      title: "Enterprise Resource Planning",
      description: "Educational systems with multi-tenant architecture and comprehensive management tools",
      background: "var(--neon-yellow)",
      color: "rgba(255, 255, 0, 0.3)",
    },
    {
      icon: <FaUsers />,
      title: "Job Portal Platforms",
      description: "AI-powered matching systems connecting employers and job seekers efficiently",
      background: "var(--neon-blue)",
      color: "rgba(0, 212, 255, 0.3)",
    },
    {
      icon: <FaCubes />,
      title: "Blockchain Solutions",
      description: "Crypto reward systems with Solana integration and smart contract development",
      background: "var(--neon-purple)",
      color: "rgba(139, 92, 246, 0.3)",
    },
    {
      icon: <FaLaptopCode />,
      title: "Custom Development",
      description: "Tailored web and mobile applications with cutting-edge technology stacks",
      background: "var(--neon-magenta)",
      color: "rgba(255, 0, 128, 0.3)",
    },
    {
      icon: <FaRocket />,
      title: "Digital Transformation",
      description: "Complete business digitization with modern design and premium user experiences",
      background: "linear-gradient(135deg, var(--neon-blue), var(--neon-green))",
      color: "rgba(0, 212, 255, 0.3)",
    }
  ];

  return (
    <AboutSection id="about">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>About Us</SectionTitle>
          <SectionSubtitle>
            We're a team of passionate developers and designers crafting cutting-edge digital solutions 
            that drive business growth and innovation through premium design and advanced technology.
          </SectionSubtitle>
        </motion.div>
        
        <AboutContent direction="column" align="center">
          <AboutInfo
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <AboutTitle>Building Next-Generation Digital Experiences</AboutTitle>
            <AboutDescription>
              At <img src={require('../../assets/logos/Labs_colour .png')} alt="GeoX Labs Logo" style={{height: '30px', verticalAlign: 'middle', margin: '0 5px'}} />, we specialize in developing cutting-edge web and mobile applications that help businesses thrive in the digital era. Our team combines technical expertise with creative design thinking to deliver solutions that are both powerful and visually stunning.
            </AboutDescription>
            <AboutDescription>
              With a focus on innovation and premium design aesthetics, we've successfully delivered solutions across diverse industries including financial technology, education, recruitment, and blockchain-powered platforms. Every project is crafted with attention to detail and modern design principles.
            </AboutDescription>
            
            <StatsContainer
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <StatCard>
                <span className="number">50+</span>
                <span className="label">Projects</span>
              </StatCard>
              <StatCard>
                <span className="number">25+</span>
                <span className="label">Clients</span>
              </StatCard>
              <StatCard>
                <span className="number">5+</span>
                <span className="label">Years</span>
              </StatCard>
              <StatCard>
                <span className="number">100%</span>
                <span className="label">Success</span>
              </StatCard>
            </StatsContainer>
          </AboutInfo>
        </AboutContent>
        
        <ExpertiseGrid>
          {expertiseItems.map((item, index) => (
            <ExpertiseItem
              key={index}
              color={item.color}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <ExpertiseIcon background={item.background}>
                {item.icon}
              </ExpertiseIcon>
              <ExpertiseContent>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </ExpertiseContent>
            </ExpertiseItem>
          ))}
        </ExpertiseGrid>
      </Container>
    </AboutSection>
  );
};

export default About;